import moment from 'moment';
import { defaultWeekEnding } from './weeklyUpdateData';

// Pure data shaping for the Schedule Status report. Kept out of the component
// so the "how far behind is this job" arithmetic — the only part anyone will
// argue about — is testable without rendering anything.

/** The week everyone should have saved by now: the Saturday of the current week
 *  (the same week-ending the Weekly Update tab defaults to). */
export const currentWeekEnding = (todayIso) => defaultWeekEnding(todayIso || moment().format('YYYY-MM-DD'));

// SQL DATE columns serialize as UTC midnight, so read them as UTC or a
// negative-offset browser shows the day before. See readScheduleStatus.sql.
const asDate = (v) => {
    if (!v) return null;
    const d = moment.utc(v);
    return d.isValid() ? d : null;
};

const iso = (v) => {
    const d = asDate(v);
    return d ? d.format('YYYY-MM-DD') : null;
};

export const STATUS = {
    current: { key: 'current', label: 'Current', color: '#166534', bg: '#dcfce7' },
    due: { key: 'due', label: '1 week behind', color: '#92400e', bg: '#fef3c7' },
    behind: { key: 'behind', label: '2+ weeks behind', color: '#b91c1c', bg: '#fee2e2' },
    never: { key: 'never', label: 'Never updated', color: '#475569', bg: '#e2e8f0' },
};

export const STATUS_ORDER = ['behind', 'due', 'never', 'current'];

/**
 * A readScheduleStatus row → the row the table renders.
 *
 * weeksBehind counts whole weeks between the job's last week-ending and the
 * current one, so a job saved for this week is 0 and a job that has never been
 * updated is null (not "infinitely behind" — those sort as their own bucket).
 */
export const statusRow = (r, weekEndingIso) => {
    const last = asDate(r.Last_Week_Ending);
    const current = moment.utc(weekEndingIso, 'YYYY-MM-DD');
    const count = Number(r.Weekly_Count) || 0;
    const weeksBehind = last && current.isValid()
        ? Math.max(0, Math.round(current.diff(last, 'days') / 7))
        : null;

    let status = 'never';
    if (count > 0 && weeksBehind !== null) {
        if (weeksBehind <= 0) status = 'current';
        else if (weeksBehind === 1) status = 'due';
        else status = 'behind';
    }

    return {
        buId: String(r.BU_ID ?? '').trim(),
        jobName: r.Job_Name || '',
        team: r.Team || '',
        jobStatus: r.Job_Status || '',
        pm: r.Project_Manager || '',
        pmEmail: r.Project_Manager_Email || '',
        lastWeekEnding: iso(r.Last_Week_Ending),
        lastSavedAt: iso(r.Last_Saved_At),
        lastSavedBy: r.Last_Saved_By || '',
        updateCount: count,
        hasSchedule: !!r.Has_Schedule,
        hasBaseline: !!r.Has_Baseline,
        hasQuantity: !!r.Has_Quantity,
        setUpAt: iso(r.Last_Setup_At),
        weeksBehind,
        status,
    };
};

export const statusRows = (jobs, todayIso) => {
    const week = currentWeekEnding(todayIso);
    return (jobs || []).map((r) => statusRow(r, week));
};

/** Headline counts, in the order the tiles are shown. */
export const summarize = (rows) => {
    const out = { total: rows.length, current: 0, due: 0, behind: 0, never: 0, setUp: 0 };
    rows.forEach((r) => {
        out[r.status] += 1;
        if (r.hasQuantity) out.setUp += 1;
    });
    return out;
};

export const teamOptions = (rows) => {
    const counts = new Map();
    rows.forEach((r) => {
        if (!r.team) return;
        counts.set(r.team, (counts.get(r.team) || 0) + 1);
    });
    return [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value));
};

export const filterRows = (rows, { search = '', teams = [], statuses = [] } = {}) => {
    const q = String(search || '').trim().toLowerCase();
    return rows.filter((r) => {
        if (teams.length && !teams.includes(r.team)) return false;
        if (statuses.length && !statuses.includes(r.status)) return false;
        if (!q) return true;
        return `${r.buId} ${r.jobName} ${r.pm} ${r.lastSavedBy}`.toLowerCase().includes(q);
    });
};

// Sort keys map to the row fields; nulls always sort last regardless of
// direction, so "never updated" jobs don't bury the jobs that are merely late.
const SORT_VALUE = {
    buId: (r) => r.buId,
    jobName: (r) => r.jobName.toLowerCase(),
    team: (r) => r.team,
    pm: (r) => r.pm.toLowerCase(),
    lastWeekEnding: (r) => r.lastWeekEnding,
    lastSavedAt: (r) => r.lastSavedAt,
    lastSavedBy: (r) => r.lastSavedBy.toLowerCase(),
    updateCount: (r) => r.updateCount,
    weeksBehind: (r) => r.weeksBehind,
    status: (r) => STATUS_ORDER.indexOf(r.status),
};

export const sortRows = (rows, key, dir = 'asc') => {
    const get = SORT_VALUE[key] || SORT_VALUE.buId;
    const sign = dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const av = get(a);
        const bv = get(b);
        const aEmpty = av === null || av === undefined || av === '';
        const bEmpty = bv === null || bv === undefined || bv === '';
        if (aEmpty && bEmpty) return a.buId.localeCompare(b.buId);
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (av === bv) return a.buId.localeCompare(b.buId);
        return (av > bv ? 1 : -1) * sign;
    });
};

/** CSV of the visible rows — this report exists to be sent to people. */
export const toCsv = (rows) => {
    const head = [
        'Job', 'Job Name', 'Team', 'Project Manager', 'Status', 'Weeks Behind',
        'Last Week Ending', 'Last Saved', 'Last Saved By', 'Updates', 'Schedule Set Up',
    ];
    const esc = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map((r) => [
        r.buId, r.jobName, r.team, r.pm, STATUS[r.status].label,
        r.weeksBehind === null ? '' : r.weeksBehind,
        r.lastWeekEnding || '', r.lastSavedAt || '', r.lastSavedBy, r.updateCount,
        r.hasQuantity ? 'Yes' : 'No',
    ].map(esc).join(','));
    return [head.join(','), ...lines].join('\n');
};
